import React from 'react';
import { Assumptions, CompModel } from '../types/compensation';
import { formatDkk, formatPercent } from '../utils/format';
interface CommissionPanelProps {
  model: CompModel;
  assumptions: Assumptions;
}
export function CommissionPanel({
  model,
  assumptions
}: CommissionPanelProps) {
  const lines = [{
    label: 'Existing customer incremental revenue',
    rate: model.existingRate,
    min: assumptions.existingRateMin,
    max: assumptions.existingRateMax,
    target: assumptions.existingRevenueTarget,
    value: model.existingCommission
  }, {
    label: 'New business / new logo',
    rate: model.newBusinessRate,
    min: assumptions.newRateMin,
    max: assumptions.newRateMax,
    target: assumptions.newBusinessTarget,
    value: model.newBusinessCommission
  }];
  return <section aria-label="Commission framework" className="rounded-card border border-line bg-ink-900 p-6 lg:p-7" data-id="element-44" data-mp="1vd12ypv9677d">
      <div className="flex items-center gap-2.5" data-id="element-45" data-mp="20cdz6180kqmm">
        <span className="h-2 w-2 rounded-full bg-performance" aria-hidden="true" data-id="element-46" data-mp="22le3t0iyrd0r" />
        <h2 className="text-[13px] font-semibold uppercase tracking-[0.2em] text-performance" data-id="element-47" data-mp="1xsujlkprtjmz">
          Performance
        </h2>
      </div>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-paper-muted" data-id="element-48" data-mp="1nvgcibpqm77c">
        Rates scale with how much of the sacrifice you put behind commission.
        The floor rates always apply — this is a revenue-generating role.
      </p>

      <div className="mt-6 space-y-5" data-id="element-49" data-mp="2gl5fgum0chbl">
        {lines.map(line => {
        const fill = (line.rate - line.min) / (line.max - line.min) * 100;
        return <div key={line.label} data-id="element-50" data-mp="27jyvuco2aqe4">
              <div className="flex items-baseline justify-between gap-4" data-id="element-51" data-mp="27iw4y4qqony7">
                <p className="text-sm text-paper" data-id="element-52" data-mp="10643bbuetz51">{line.label}</p>
                <p className="tnum text-lg font-semibold text-performance" data-id="element-53" data-mp="w4a8hk6wbpcs">
                  {formatPercent(line.rate)}
                </p>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-line" role="presentation" data-id="element-54" data-mp="xlz3wbgnk27d">
                <div className="h-full rounded-full bg-performance transition-[width] duration-200 ease-out" style={{
              width: `${Math.max(fill, 2)}%`
            }} data-id="element-55" data-mp="nnrp9z9u5w8j" />
              </div>
              <div className="tnum mt-2 flex justify-between text-xs text-paper-dim" data-id="element-56" data-mp="etwt3gqozg51">
                <span data-id="element-57" data-mp="jlwnyurwniur">
                  Floor {formatPercent(line.min, 0)} · ceiling{' '}
                  {formatPercent(line.max, 0)}
                </span>
                <span data-id="element-58" data-mp="4uxn65xb9ylq">
                  {formatDkk(line.target)} target →{' '}
                  <span className="text-paper-muted" data-id="element-59" data-mp="9tqz3twy9hjs">
                    {formatDkk(line.value)}
                  </span>
                </span>
              </div>
            </div>;
      })}
      </div>

      <div className="mt-6 flex items-baseline justify-between gap-4 border-t border-line-soft pt-4" data-id="element-60" data-mp="3w4w8sbpsgbfn">
        <p className="text-sm text-paper-muted" data-id="element-61" data-mp="3dgm1h2xv0a5x">
          Expected annual commission at target
        </p>
        <p className="tnum text-xl font-semibold text-paper" data-id="element-62" data-mp="3mffteg08wcd5">
          {formatDkk(model.expectedCommission)}
        </p>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-paper-dim" data-id="element-63" data-mp="3hfbdar391jng">
        Modelled commission opportunity from the allocated pool ×
        {assumptions.commissionMultiplier}:{' '}
        {formatDkk(model.commissionOpportunity)}. Accelerators above target are
        agreed separately.
      </p>
    </section>;
}